import { useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  addDays,
  countedBlocks,
  formatLongDate,
  formatTimeRange,
  minutesToClockCompact,
  type CalendarAllDayEvent,
  type CalendarBlock,
  type ClassOccurrence,
  type ISODate,
} from '@app/shared'
import clsx from 'clsx'

import { useCalendarRange, useClassOccurrences, useClock } from '../../lib/data'
import { PALETTE, type PaletteRow } from '../../components/calendar/palette'
import { useDismissable, useEntered } from '../../lib/popover'
import { MBell, MCalendar, MChevronLeft, MChevronRight } from '../shell/icons'
import {
  BackRow,
  Band,
  BandHeading,
  BandTile,
  Dot,
  ReadFailure,
  ScrollColumn,
  Skeleton,
} from '../shell/parts'

// ============================================================================
// THE PHONE'S CALENDAR — ONE DAY, READ (DAY-1: "read the day's schedule").
//
// WHY IT EXISTS. `/calendar` landed on DesktopOnly, which meant that everything the owner puts
// on his calendar — his own blocks, the Google mirror, his class times — was invisible on the
// device he carries. The reason DesktopOnly gave was true about the thing it named: a WEEK GRID
// of blocks that are dragged and resized needs a pointer and a week's worth of width. A day
// AGENDA is not that thing. So the grid is still the desktop's, and this is a list.
//
// THE HANDOFF DRAWS NO MOBILE CALENDAR (docs/design/handoff-mobile/ has eleven screens and this
// is not one of them), so nothing here is a port of a mockup. It is built out of the shell
// primitives every other phone screen is built out of (mobile/shell/parts.tsx) and it obeys the
// calendar brief's honesty rules, which are about the data rather than the pixels:
//
//   1. READ-ONLY IS A FACT, NOT A DISABLED BUTTON. Nothing on this screen writes. A row opens a
//      detail sheet that states what is stored and says outright where the block can be changed.
//      See `DetailSheet` for the one deviation that cost, and the PR for why.
//   2. NOTHING INVENTS A TIME. All-day events are listed as all-day, first, with no hour
//      attached to them; a timed event's own start and end are the only clock this screen knows.
//   3. HIDDEN IS NOT MISSING. A failed read says so instead of drawing an empty day, because an
//      empty day is a claim ("you have nothing on") that a failed read is not entitled to make.
//
// ── THE ONE NON-OBVIOUS THING: WHY TWO READS, AND WHAT THE SECOND ONE IS FOR ──────────────
//
// `useCalendarRange({from: date, to: date})` already answers the whole day: LifeOS's own blocks,
// timed Google events, and — in demo — class blocks derived from the course timetable.
//
// On REAL data there are no Canvas class blocks at all: Berkeley's meeting times are not in
// Canvas (they live in SIS and reach a student through bCal), so the Worker derives classes from
// the mirrored Google Calendar instead — `GET /api/school/class-occurrences?date=`, which keeps
// the `gcal_events` rows whose title opens with a synced course code. That is the SAME ROW the
// calendar range hands back as a `source: 'google'` block.
//
// So reading both and printing both would list every lecture twice on the owner's real phone,
// and reading only the range would print "COGSCI 1 Lecture – Google" where the app knows
// perfectly well that it is COGSCI 1's lecture in VLSB 2050. Hence `classSpans` below: a
// NON-EDITABLE block whose start and end match a class occurrence on the same day IS that class,
// and the class row — which names the course, the kind and the room — is the one that is drawn.
// Only mirrored rows are ever suppressed; a block LifeOS owns is never hidden by this rule, so a
// personal block that happens to sit exactly across a lecture still appears.
// ============================================================================

/** What one line of the agenda is. All-day first, then the timed rows in clock order. */
type AgendaRow =
  | { kind: 'allDay'; key: string; event: CalendarAllDayEvent }
  | { kind: 'class'; key: string; occurrence: ClassOccurrence }
  | { kind: 'block'; key: string; block: CalendarBlock }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * A row's two edges in minutes past local midnight, or null when it has none. `null` is a real
 * answer and the reason this exists: an all-day event HAS no time of day, and a function that
 * handed back midnight-to-midnight for one would be the screen inventing a clock Google never
 * gave (brief rule 2, restated in the header).
 */
function minutesOf(row: AgendaRow): { startMinutes: number; endMinutes: number } | null {
  if (row.kind === 'allDay') return null
  return row.kind === 'class' ? row.occurrence : row.block
}

/** '10:00 – 11:00 AM' for a span of clock time; the words for a day that has no clock. */
function spanOf(row: AgendaRow): string {
  const minutes = minutesOf(row)
  return minutes ? formatTimeRange(minutes.startMinutes, minutes.endMinutes) : 'All day'
}

const CLASS_KIND_WORD: Record<ClassOccurrence['kind'], string> = {
  lecture: 'Lecture',
  discussion: 'Discussion',
  lab: 'Lab',
}

/**
 * ONE REMINDER OFFSET, IN WORDS. The same vocabulary the picker offers
 * (components/calendar/Reminders.tsx → PRESETS), compact enough for a phone row: the picker says
 * "30 minutes before" in a form, this says "30 min before" in a line of metadata, and `0` is "at
 * start" in both. Nothing here rounds: a 90-minute lead reads "1 h 30 min before" rather than
 * being flattened to an hour.
 */
export function reminderOffsetPhrase(minutes: number): string {
  if (minutes <= 0) return 'at start'
  if (minutes % 1440 === 0) {
    const days = minutes / 1440
    return `${days} ${days === 1 ? 'day' : 'days'} before`
  }
  if (minutes < 60) return `${minutes} min before`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (rest === 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'} before`
  return `${hours} h ${rest} min before`
}

/**
 * The bell hint on a row. Two offsets are spelled out; beyond that the row says how many more
 * there are rather than truncating the list into something that looks complete.
 */
export function reminderHint(reminders: readonly number[]): string | null {
  if (reminders.length === 0) return null
  const sorted = [...reminders].sort((a, b) => b - a)
  const shown = sorted.slice(0, 2).map(reminderOffsetPhrase).join(', ')
  const rest = sorted.length - 2
  return rest > 0 ? `Reminds ${shown}, +${rest} more` : `Reminds ${shown}`
}

/** Which palette row a line wears. Classes borrow Canvas's blue: they are a course's time. */
function paletteOf(row: AgendaRow): PaletteRow {
  if (row.kind === 'class') return PALETTE.canvas
  if (row.kind === 'allDay') return PALETTE.google
  return PALETTE[row.block.source]
}

/**
 * WHERE THIS ROW CAME FROM, in the words the source uses about itself. `null` for a block the
 * owner made, because "Personal" is already the lane it is drawn in and stamping every row with
 * its own colour's name is noise. Google's status word rides along when it is not `confirmed`:
 * "Google said maybe" is a state the screen draws rather than quietly promoting.
 */
function provenanceOf(row: AgendaRow): string | null {
  if (row.kind === 'class') return 'Class'
  if (row.kind === 'allDay') {
    return row.event.status === 'confirmed' ? 'Google' : `Google · ${row.event.status}`
  }
  const { source, status } = row.block
  if (source === 'google') return status && status !== 'confirmed' ? `Google · ${status}` : 'Google'
  if (source === 'canvas') return 'Canvas'
  return null
}

function titleOf(row: AgendaRow): string {
  if (row.kind === 'allDay') return row.event.title
  if (row.kind === 'block') return row.block.title
  return `${row.occurrence.courseCode} ${CLASS_KIND_WORD[row.occurrence.kind]}`
}

function locationOf(row: AgendaRow): string | null {
  if (row.kind === 'allDay') return row.event.location
  if (row.kind === 'block') return row.block.location
  return row.occurrence.location || null
}

export function MobileCalendarDay() {
  const { data: clock } = useClock()

  // THE DAY LIVES IN THE URL, for the reason the journal archive's month does: opening a row and
  // coming back must not silently return the owner to today, `?date=` is an address he can send
  // himself, and a screenshot harness has no arrow key to press. It also makes `/calendar?date=`
  // mean the same thing on the phone as it does on the desktop, which is the whole point of the
  // route being the same route.
  const [params, setParams] = useSearchParams()
  const asked = params.get('date')
  const date: ISODate | null = ISO_DATE.test(asked ?? '')
    ? (asked as ISODate)
    : (clock?.date ?? null)

  const goToDay = (next: ISODate) => setParams({ date: next }, { replace: true })

  const range = useMemo(() => (date ? { from: date, to: date } : undefined), [date])
  const { data: calendar, isPending, error } = useCalendarRange(range)
  const {
    data: classes,
    isPending: classesPending,
    error: classesError,
  } = useClassOccurrences(date ?? undefined)

  const [open, setOpen] = useState<AgendaRow | null>(null)

  const rows = useMemo<AgendaRow[]>(() => {
    if (!calendar || !date) return []

    const occurrences = classes ?? []
    // See the header: a mirrored row and the class derived from it are one meeting.
    const classSpans = new Set(occurrences.map((o) => `${o.startMinutes}-${o.endMinutes}`))

    const timed: AgendaRow[] = [
      ...occurrences.map((occurrence) => ({
        kind: 'class' as const,
        key: `class-${occurrence.courseId}-${occurrence.kind}-${occurrence.startMinutes}`,
        occurrence,
      })),
      // `countedBlocks` drops anything still merely SUGGESTED. A proposal nobody accepted is not
      // on the calendar, and a list that showed one would be counting it (brief rule 2).
      ...countedBlocks(calendar.blocks)
        .filter((b) => b.date === date)
        .filter((b) => b.editable || !classSpans.has(`${b.startMinutes}-${b.endMinutes}`))
        .map((block) => ({ kind: 'block' as const, key: `block-${block.id}`, block })),
    ].sort((a, b) => {
      // Every row in THIS list is timed — the all-day ones are assembled separately below — so
      // the zero fallbacks never fire. They are here because `minutesOf` is honest about all-day
      // rows and a `!` would be a promise this sort cannot keep if the list ever widens.
      const aa = minutesOf(a)
      const bb = minutesOf(b)
      return (
        (aa?.startMinutes ?? 0) - (bb?.startMinutes ?? 0) ||
        (aa?.endMinutes ?? 0) - (bb?.endMinutes ?? 0) ||
        titleOf(a).localeCompare(titleOf(b))
      )
    })

    const allDay: AgendaRow[] = calendar.allDayEvents.map((event) => ({
      kind: 'allDay' as const,
      key: `allday-${event.id}`,
      event,
    }))

    return [...allDay, ...timed]
  }, [calendar, classes, date])

  // A FAILED READ IS NOT AN EMPTY DAY, and it is only the CALENDAR read that can empty this
  // screen: the class read is a refinement on top of it (see the header), so when that one alone
  // fails the day still lists everything the calendar returned — including the mirrored rows the
  // class read would have relabelled. That is stated on screen rather than glossed over.
  const failed = Boolean(error)
  const pending = isPending || classesPending

  return (
    <>
      {/* THE WAY OUT. A pushed screen has no tab bar (V10 board 25); on a cold open there is
          nothing behind this page, and BackRow goes to Today (mobile/shell/nav.ts → backTarget). */}
      <Band band="today" className="px-[14px] pb-3 pt-[6px]">
        <BackRow label="Today" />
        <BandHeading
          title={date ? formatLongDate(date) : '…'}
          action={
            <div className="flex flex-none items-center gap-2">
              <BandTile
                label="Previous day"
                onClick={() => date && goToDay(addDays(date, -1))}
              >
                <MChevronLeft />
              </BandTile>
              <BandTile label="Next day" onClick={() => date && goToDay(addDays(date, 1))}>
                <MChevronRight />
              </BandTile>
            </div>
          }
          subline={
            <>
              <span className="font-medium">Calendar</span>
              <Dot />
              {/* TODAY IS DRAWN ONLY WHEN IT IS SOMEWHERE ELSE. On today's own day there is
                  nothing for it to do, and this product does not draw affordances that do
                  nothing (mobile/screens/MobileJournalArchive.tsx makes the same call about the
                  forward arrow on the current month). */}
              {clock && date !== clock.date ? (
                <button
                  type="button"
                  onClick={() => goToDay(clock.date)}
                  className="rounded-pill bg-surface/80 px-2 py-[3px] font-medium text-ink-2"
                >
                  Today
                </button>
              ) : (
                <span>Today</span>
              )}
              {!pending && !failed && (
                <>
                  <Dot />
                  <span>
                    {rows.length} {rows.length === 1 ? 'item' : 'items'}
                  </span>
                </>
              )}
            </>
          }
        />
      </Band>

      <ScrollColumn className="pt-[2px]">
        {pending && (
          <>
            <Skeleton height={62} className="rounded-row" />
            <Skeleton height={62} className="rounded-row" />
            <Skeleton height={62} className="rounded-row" />
          </>
        )}

        {failed && <ReadFailure error={error} what="The day’s calendar" />}

        {/* The narrower failure: the day is readable, the course names are not. */}
        {!failed && classesError && (
          <p className="px-1 text-[11.5px] leading-[1.5] text-microcopy text-pretty">
            Your class times could not be read, so any lecture below is listed the way its
            calendar entry is written rather than by course.
          </p>
        )}

        {!pending && !failed && rows.length === 0 && (
          <p className="px-1 pt-2 text-[12.5px] leading-[1.5] text-microcopy text-pretty">
            Nothing is on this day. That is the calendar’s answer, not a blank — classes, your own
            blocks and anything mirrored from Google would all be here.
          </p>
        )}

        {rows.map((row) => (
          <AgendaLine key={row.key} row={row} onOpen={() => setOpen(row)} />
        ))}
      </ScrollColumn>

      {open && <DetailSheet row={open} onClose={() => setOpen(null)} />}
    </>
  )
}

/**
 * One line of the day. The whole row is the tap target — a phone has no hover to reveal one —
 * and it opens a sheet that reads the row out in full rather than changing anything.
 */
function AgendaLine({ row, onOpen }: { row: AgendaRow; onOpen: () => void }) {
  const palette = paletteOf(row)
  const location = locationOf(row)
  const provenance = provenanceOf(row)
  const hint = row.kind === 'block' ? reminderHint(row.block.reminders ?? []) : null

  return (
    <button
      type="button"
      onClick={onOpen}
      className={clsx(
        'flex flex-none gap-3 rounded-row border-l-[3px] px-3 py-[10px] text-left active:opacity-80',
        palette.tint,
        palette.rule,
      )}
    >
      {/* The compact register ('12:00p'), stacked — the same 56px time column the Today screen's
          classes card uses, and the same reason: '12:00 PM' does not fit in it at the owner's
          130% scale. An all-day row has no clock at all and says so instead of borrowing one. */}
      <span className={clsx('w-[56px] flex-none text-[11.5px] font-semibold leading-[1.4]', palette.ink2)}>
        {row.kind === 'allDay' ? (
          'All day'
        ) : row.kind === 'class' ? (
          <>
            {minutesToClockCompact(row.occurrence.startMinutes)}
            <br />
            {minutesToClockCompact(row.occurrence.endMinutes)}
          </>
        ) : (
          <>
            {minutesToClockCompact(row.block.startMinutes)}
            <br />
            {minutesToClockCompact(row.block.endMinutes)}
          </>
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className={clsx('block truncate text-[15px] font-semibold leading-[1.2]', palette.ink)}>
          {titleOf(row)}
        </span>
        {(location || provenance) && (
          <span className={clsx('mt-1 block truncate text-[11.5px] font-normal leading-[1.4]', palette.ink2)}>
            {[location, provenance].filter(Boolean).join(' · ')}
          </span>
        )}
        {hint && (
          <span className={clsx('mt-1 flex items-center gap-[5px] text-[11px] font-medium leading-[1.4]', palette.ink2)}>
            <MBell size={13} strokeWidth={1.8} className="flex-none" />
            {hint}
          </span>
        )}
      </span>
    </button>
  )
}

/**
 * THE DETAIL SHEET, AND IT IS READ-ONLY — the deviation this screen makes, named here and in the
 * PR rather than left to be discovered.
 *
 * The brief for this screen asked for the desktop `Composer` (start presets, reminder picker) if
 * it could render inside the mobile shell without desktop-only assumptions. It cannot, and the
 * assumption is a measured one rather than a matter of taste: `ComposerShell` hard-codes
 * `w-[360px]`, and the owner runs the app at 130% (Appearance → Size, his default), which makes
 * his 390px phone a 300px CSS viewport — a 272px card. A 360px panel inside it is 88px of
 * horizontal scroll on every open, and the width is baked into the shared component's own class
 * string, so honouring it would mean either editing the desktop composer's layout or wrapping it
 * in something that lies about its size.
 *
 * So the phone reads the day and the desktop edits it, which is what DesktopOnly said about the
 * grid all along and is still true of the part that is genuinely a pointer job. Everything the
 * API stores about a row is printed here — including every reminder offset, not a summary of
 * them — so nothing is hidden behind the absent form.
 */
function DetailSheet({ row, onClose }: { row: AgendaRow; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null)
  useDismissable(ref, onClose)
  const entered = useEntered()

  const palette = paletteOf(row)
  const location = locationOf(row)
  const provenance = provenanceOf(row)
  const reminders = row.kind === 'block' ? [...(row.block.reminders ?? [])].sort((a, b) => b - a) : []

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="Calendar item">
      <button
        type="button"
        aria-label="Close calendar item"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-scrim backdrop-blur-[1.5px] motion-safe:animate-[fade-in_var(--dur-scrim)_var(--ease-out)]"
      />

      <div
        ref={ref}
        className={clsx(
          'relative flex max-h-full flex-col gap-[11px] overflow-y-auto rounded-t-pill bg-surface px-[18px] pb-[18px] pt-[14px] shadow-sheet',
          'transition-opacity duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
          entered ? 'opacity-100' : 'opacity-0',
        )}
        style={{ paddingBottom: 'calc(18px + env(safe-area-inset-bottom, 24px))' }}
      >
        <span className="mx-auto h-1 w-[38px] flex-none rounded-[2px] bg-line-2" aria-hidden />

        <div className="flex items-center gap-2">
          <MCalendar size={16} strokeWidth={1.8} className={clsx('flex-none', palette.ink)} />
          <span className="text-[11px] font-semibold uppercase leading-none tracking-[0.06em] text-microcopy">
            {provenance ?? 'Your block'}
          </span>
        </div>

        {/* `break-words` because a title is somebody else's string: a mirrored Google event can
            be one unbroken 80-character URL, and an unbreakable word is the one thing that makes
            a fluid column wider than the phone. */}
        <h2 className="break-words text-[19px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-pretty">
          {titleOf(row)}
        </h2>

        <p className="text-[12.5px] font-medium leading-[1.5] text-ink-2">{spanOf(row)}</p>

        {row.kind === 'class' && (
          <p className="text-[12.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
            {row.occurrence.courseName}
          </p>
        )}

        {location && (
          <p className="break-words text-[12.5px] font-normal leading-[1.5] text-ink-3">
            {location}
          </p>
        )}

        {reminders.length > 0 && (
          <div className="flex flex-col gap-[6px]">
            {reminders.map((minutes, i) => (
              <span
                key={`${minutes}-${i}`}
                className="flex items-center gap-[6px] text-[12px] font-medium leading-none text-ink-2"
              >
                <MBell size={14} strokeWidth={1.8} className="flex-none" />
                Reminds {reminderOffsetPhrase(minutes)}
              </span>
            ))}
          </div>
        )}

        {/* WHERE IT CAN BE CHANGED, said plainly. Not a disabled Save button — see the header. */}
        <p className="text-[11.5px] font-normal leading-[1.5] text-microcopy text-pretty">
          {row.kind === 'block' && row.block.editable
            ? 'This is your block. The phone shows the day and does not change it — editing, moving and reminders are on the desktop calendar.'
            : 'Nothing here can be changed anywhere in LifeOS: this is mirrored from the calendar it lives on, and the app never writes back to it.'}
        </p>

        <button
          type="button"
          onClick={onClose}
          className="mt-1 flex h-11 flex-none items-center justify-center rounded-tile bg-ink px-5 text-[13.5px] font-semibold leading-none text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)]"
        >
          Close
        </button>
      </div>
    </div>
  )
}
